import type { ComponentType, PropsWithChildren } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { EventTarget } from "event-target-shim";
import { identity } from "lodash";
import { InputRule } from "prosemirror-inputrules";
import { MarkType, type NodeSpec, NodeType, Node as ProseMirrorNode, Schema } from "prosemirror-model";
import { EditorState, Plugin, Transaction } from "prosemirror-state";
import { Decoration, EditorView, type NodeView } from "prosemirror-view";

import { CtrlOrCmd } from "~/lib/utils";

import { getContext } from "./plugins/context";
import type { ProseReact } from "./prose-react";
import type { Command } from "./types";

/**
 * A function returning a NodeView for rendering the Node type defined by a NodeExtension.
 *
 * @param node - The node being rendered
 * @param view - The editor's EditorView
 * @param getPos - For nodes, a function that can be called to get the node's current position – that is, the position
 *                 immediately **before** the opening token of the node. For marks, it's a boolean that indicates
 *                 whether the mark's content is inline.
 * @param decorations - An array of node or inline decorations that are active around the node. They are automatically
 *                      drawn in the normal way, and you will usually just want to ignore this, but they can also be
 *                      used as a way to provide context information to the node view without adding it to the document
 *                      itself.
 * @see {@link https://prosemirror.net/docs/ref/#view.EditorProps.nodeViews}
 */
export type ExtensionNodeView = (
	node: ProseMirrorNode,
	view: EditorView,
	getPos: boolean | (() => number),
	decorations: readonly Decoration[],
) => NodeView;

declare module "prosemirror-state" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface PluginSpec<PluginState> {
		historyPreserveItems?: boolean;
	}
}

export type MenuEntryComponentProps = PropsWithChildren<{
	entry: MenuEntry;
	isMobile: boolean;
	editorView: EditorView;
	extension: MenuExtension;
	active: boolean;
	disabled: boolean;
	asMenuItem?: boolean;
}>;

/**
 * A generic interface for toolbar entries, independent of the implementer.
 */
export interface MenuEntry {
	/**
	 * A FontAwesome icon.
	 */
	icon: IconDefinition;

	/**
	 * A label/title to display with the menu entry
	 */
	title: string;

	/**
	 * A subtitle to display with the menu entry in the plus menu
	 */
	subtitle?: string;

	showInToolbar?: boolean;

	/**
	 * A custom component, that used to draw instead of `<button><icon/></button>`.
	 */
	component?: ComponentType<MenuEntryComponentProps>;

	/**
	 * The keyboard shortcut that corresponds to this menu entry
	 */
	keyboardShortcut?: string;

	/**
	 * A label to display when hovering over the menu entry
	 */
	tooltip?: string;

	/**
	 * The priority (higher being first) this entry should have when listed in a
	 * menu. Entries with the same priority will be listed in the order they
	 * are discovered. Menu entries without a priority specified will default
	 * to 0.
	 */
	priority?: number;

	/**
	 * The section decides which part of the formatting toolbar the menu entry
	 * appears in. The `group` property below is used for grouping within that
	 * section.
	 */
	section?: "types" | "mostUsedAndSocial" | "reaction" | "formatting" | "colouring" | "indent";

	/**
	 * If provided, a group label for this entry. Groups with the same label
	 * should be grouped together with a visual separator.
	 * When group is "insert", the command is assumed to insert items instead of switching types
	 */
	group?: string;

	/**
	 * If provided, a ColouringMenu component will be rendered with these colour options.
	 */
	colourOptions?: string[];

	/**
	 * If true this icon will only appear when the menu is in it's mobile mode
	 */
	mobileOnly?: boolean;

	/**
	 * Hide on < 736px screen width if true.
	 */
	hideOnMediumScreen?: boolean;

	/**
	 * Hide on <= 280px screen width (MS Teams) if true.
	 */
	hideOnExtraSmallScreen?: boolean;

	/**
	 * Commands have the ability to do a dry run, where the result is not
	 * dispatched, but the command returns true if it can take action
	 * and false if it can't. This option utilizes that functionality
	 * to grey out the menu options in the cases where it won't do anything.
	 */
	greyIfDryRunFalse?: boolean;

	/**
	 * Called when [[event]] has been triggered on this menu entry. It should
	 * return  a command().
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	run(...rest: any[]): Command;
	/**
	 * An optional method which should return True if the current menu item is
	 * "active" (ex: for bold, text is already bold).
	 */
	active?(state: EditorState): boolean;
	overrideContent?(state: EditorState): HTMLElement;
}

export interface KeysArgs {
	$type?: MarkType | NodeType;
}

export interface Keybinding {
	[key: string]: (
		state: EditorState,
		dispatch: ((tr: Transaction) => void) | undefined,
		view: EditorView | undefined,
	) => boolean;
}

declare module "prosemirror-view" {
	interface EditorView {
		extensions: Map<string, Extension>;
	}
}

/**
 * An extension is a class that can be used to extend the editor's functionality.
 */
export abstract class Extension<S extends Schema = Schema<string, string>> extends EventTarget {
	// Extension should be generic on the type of its options, but TS has no easy way of having associated types,
	// so we're using an explicit any here for now.

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: { [key: string]: any };
	editor?: ProseReact;
	editorView?: EditorView;
	nodeViews?: { [key: string]: ExtensionNodeView };
	schema?: S;
	editorContainer?: HTMLElement;

	constructor(options = {}) {
		super();
		this.options = {
			...this.defaultOptions,
			...options,
		};
	}

	/**
	 * Called by the editor after the editorView has been fully initialized.
	 */
	init(): void {}

	/**
	 * Called by the editor as it is being destroyed.
	 */
	destroy(): void {}

	/**
	 * An optional hash of options that should be used as defaults when
	 * creating new instances of an extension.
	 */
	get defaultOptions() {
		return {};
	}

	/**
	 * A unique name identifying the extension.
	 *
	 * For NodeExtensions, this name will be used as the schema key.
	 */
	abstract get name(): string;

	/**
	 * An optional list of ProseMirror Plugin instances that should be
	 * registered on the editor.
	 */
	get plugins(): Plugin[] {
		return [];
	}

	/**
	 * called by the editor when its container is blurred.
	 */
	onEditorBlur(_e: React.FocusEvent): void {}
	/**
	 * called by the editor when its container is focused.
	 */
	onEditorFocus(_e: React.FocusEvent): void {}

	/**
	 * Called by `MenuExtension`s to get an icon and update method to render.
	 */
	menuEntry(_view: EditorView): MenuEntry | MenuEntry[] | null {
		return null;
	}

	keys(_m: KeysArgs): Keybinding | null {
		return null;
	}

	cmdPlus(key: string): string {
		return `${CtrlOrCmd} + ${key}`;
	}

	/**
	 * A set of InputRules to respond to certain text inputs from the user.
	 *
	 * Note that this is intentionally a method (as opposed to a getter). In the
	 * common case, `this.$type` will be accessed in this method, and if that
	 * happens at construction time, `this.$type` will just be `null` since the
	 * editorView hasn't been assigned yet.
	 */
	inputRules?(): readonly InputRule[];
}

/**
 * A NodeExtension is an extension that adds a node to the editor's schema.
 */
export abstract class NodeExtension<S extends Schema = Schema<string, string>> extends Extension<S> {
	/**
	 * An optional ProseMirror NodeView or ReactNodeView used to handle this
	 * type of node.
	 */
	view: ExtensionNodeView | null = null;

	/**
	 * The node schema implemented by this extension.
	 */
	abstract get node(): NodeSpec;

	get $type() {
		if (!this.schema) {
			throw new Error("Access schema before initialization");
		}
		return this.schema.nodes[this.name];
	}

	keys(_m: KeysArgs): Keybinding | null {
		return null;
	}
}

/**
 * A MarkExtension is an extension that adds a mark to the editor's schema.
 */
export abstract class MarkExtension<S extends Schema = Schema<string, string>> extends Extension<S> {
	/**
	 * The mark schema implemented by this extension.
	 */
	abstract get mark(): { [key: string]: unknown };

	get $type() {
		if (!this.editorView) {
			return null;
		}
		return this.editorView.state.schema.marks[this.name];
	}

	keys(_m: KeysArgs): Keybinding | null {
		return null;
	}
}

/**
 * A MenuExtension is an extension that adds a menu to the editor.
 * The formatting toolbar is a kind of menu, for example.
 * These extensions have access to other extensions that have a MenuEntry
 */
export abstract class MenuExtension<S extends Schema = Schema<string, string>> extends Extension<S> {
	entryExtensions = new WeakMap<MenuEntry, Extension>();

	protected get allEntries() {
		const { editor } = this;
		if (!editor) return [];
		const { editorView } = this;
		if (!editorView) return [];

		const context = getContext(editorView.state);

		const allEntries: MenuEntry[] = [];
		editor.extensions.forEach(extension => {
			if (context.isMeetingModal && extension.name === "block-comments") {
				return;
			}
			let entries = extension.menuEntry(editorView);
			if (!entries) return;
			if (!Array.isArray(entries)) entries = [entries];
			allEntries.push(...entries);
			entries.forEach(e => this.entryExtensions.set(e, extension));
		});
		const func: (v: MenuEntry[]) => MenuEntry[] = this.options.postProcessEntries ?? identity;
		return func(allEntries);
	}
}
